import type { DashboardCountrySummary } from '@livechat/shared';

import { WORLD_COUNTRY_SHAPES } from '../world-map-country-shapes.js';

export function countryDensity(visitorCount: number, maximumVisitorCount: number): number {
	if (visitorCount <= 0 || maximumVisitorCount <= 0) return 0;
	return Math.max(1, Math.min(5, Math.ceil((visitorCount / maximumVisitorCount) * 5)));
}

export function WorldVisitorMap({ countries }: { countries: DashboardCountrySummary[] }) {
	const countryByCode = new Map(
		countries.map((country) => [country.countryCode.toUpperCase(), country]),
	);
	const maximumVisitorCount = Math.max(0, ...countries.map((country) => country.visitorCount));
	const visitorTotal = countries.reduce((total, country) => total + country.visitorCount, 0);

	return (
		<div className="dashboard-map-wrap">
			<svg
				aria-labelledby="visitor-map-title visitor-map-description"
				className="dashboard-world-map"
				role="img"
				viewBox="0 0 2000 1001"
			>
				<title id="visitor-map-title">Visitor locations by country</title>
				<desc id="visitor-map-description">
					Countries with more unique visitors are shaded a darker blue.
				</desc>
				{WORLD_COUNTRY_SHAPES.map(({ id, shape }) => {
					const country = countryByCode.get(id);
					const density = country
						? countryDensity(country.visitorCount, maximumVisitorCount)
						: 0;
					return (
						<path
							aria-label={
								country
									? `${country.countryName}: ${country.visitorCount} unique ${
											country.visitorCount === 1 ? 'visitor' : 'visitors'
										}`
									: undefined
							}
							className={`dashboard-map-country map-density-${density}`}
							d={shape}
							key={id}
							tabIndex={country ? 0 : undefined}
						>
							{country && (
								<title>{`${country.countryName}: ${country.visitorCount} unique ${
									country.visitorCount === 1 ? 'visitor' : 'visitors'
								}`}</title>
							)}
						</path>
					);
				})}
			</svg>
			<div className="dashboard-map-footer">
				<span>
					<strong>{visitorTotal}</strong> located{' '}
					{visitorTotal === 1 ? 'visitor' : 'visitors'}
					{' · '}
					<strong>{countries.length}</strong>{' '}
					{countries.length === 1 ? 'country' : 'countries'}
				</span>
				<div aria-label="Visitor density legend" className="dashboard-map-legend">
					<span>Fewer</span>
					{[1, 2, 3, 4, 5].map((density) => (
						<i className={`map-density-${density}`} key={density} />
					))}
					<span>More</span>
				</div>
			</div>
		</div>
	);
}
